import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "~/api/client";
import { useUnifiedWallet } from "./useUnifiedWallet";
import type { WalletBalance } from "~/api/types";

export function useWalletBalances() {
  const { address, isConnected } = useUnifiedWallet();

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["balances", address],
    queryFn: () => api.getBalances(address!),
    enabled: isConnected && !!address,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  const balances = useMemo(() => {
    if (!data?.balances) return [];
    return data.balances.filter((b) => b.valueUsd >= 0.01).sort((a, b) => b.valueUsd - a.valueUsd);
  }, [data]);

  /** Map of "chainId:address" → balance for quick lookups */
  const balanceMap = useMemo(() => {
    const map = new Map<string, WalletBalance>();
    for (const b of balances) {
      map.set(`${b.chainId}:${b.address.toLowerCase()}`, b);
    }
    return map;
  }, [balances]);

  /** Map of chainId → total USD value on that chain */
  const chainBalanceMap = useMemo(() => {
    const map = new Map<string, number>();
    for (const b of balances) {
      map.set(b.chainId, (map.get(b.chainId) ?? 0) + b.valueUsd);
    }
    return map;
  }, [balances]);

  return {
    balances,
    balanceMap,
    chainBalanceMap,
    totalValueUsd: data?.totalValueUsd ?? 0,
    isLoading,
    refetch,
  };
}
